import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../config/api';

const AuthCallback = () => {
    const { checkAuthStatus } = useAuth();
    const [isVerifying, setIsVerifying] = useState(true);
    const [redirectTo, setRedirectTo] = useState(null);

    useEffect(() => {
        const handleAuthCallback = async () => {
            try {
                console.log('Starting auth callback verification...');
                
                // Wait longer for Azure session to be established
                await new Promise(resolve => setTimeout(resolve, 1500));
                
                // Make direct API call to verify authentication
                let authVerified = false;
                let attempts = 0;
                const maxAttempts = 5;
                
                while (!authVerified && attempts < maxAttempts) {
                    attempts++;
                    console.log(`Auth verification attempt ${attempts}/${maxAttempts}`);
                    
                    try {
                        const response = await apiClient.get('/api/auth/status');
                        console.log('Auth status response:', response.data);
                        
                        if (response.data.isAuthenticated) {
                            authVerified = true;
                            console.log('Authentication verified successfully');
                            
                            // Authentication is verified, now trigger the AuthContext to check permissions
                            // Trigger the AuthContext to check permissions by calling checkAuthStatus
                            const authSuccess = await checkAuthStatus();
                            
                            if (authSuccess) {
                                // User has permissions, proceed with redirect
                                const redirectUrl = sessionStorage.getItem('loginRedirect');
                                sessionStorage.removeItem('loginRedirect');
                                
                                const destination = redirectUrl || '/dashboard';
                                setRedirectTo(destination);
                            } else {
                                // User doesn't have permissions, redirect to permission denied
                                setRedirectTo('/permission-denied');
                            }
                            return; // Exit early on success
                        } else {
                            console.log('Not authenticated yet, waiting...');
                        }
                    } catch (error) {
                        console.error(`Auth verification attempt ${attempts} failed:`, error);
                        
                        // Handle rate limiting specifically
                        if (error.response && error.response.status === 429) {
                            console.log('Rate limited, waiting longer before retry...');
                            if (attempts < maxAttempts) {
                                // Wait longer for rate limit errors
                                await new Promise(resolve => setTimeout(resolve, 5000));
                            }
                        } else {
                            // Regular error, shorter wait
                            if (attempts < maxAttempts) {
                                await new Promise(resolve => setTimeout(resolve, 2000));
                            }
                        }
                    }
                    
                    // Only wait if we haven't reached max attempts and haven't verified auth
                    if (!authVerified && attempts < maxAttempts) {
                        // Exponential backoff: 1s, 2s, 4s, 8s
                        const waitTime = Math.pow(2, attempts - 1) * 1000;
                        await new Promise(resolve => setTimeout(resolve, waitTime));
                    }
                }
                
                if (!authVerified) {
                    console.error('Authentication verification failed after all attempts');
                    setRedirectTo('/login?error=auth_timeout');
                }
                
            } catch (error) {
                console.error('Auth callback error:', error);
                setRedirectTo('/login?error=callback_failed');
            } finally {
                setIsVerifying(false);
            }
        };

        handleAuthCallback();
    }, []); // Remove checkAuthStatus dependency to prevent infinite loops

    // Show loading while verifying
    if (isVerifying) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    gap: 2
                }}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    Verifying authentication...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Please wait a moment
                </Typography>
            </Box>
        );
    }

    // Redirect when ready
    if (redirectTo) {
        return <Navigate to={redirectTo} replace />;
    }

    return null;
};

export default AuthCallback;
